import React, { useState, useEffect } from 'react';
import api from '../../../../core/api/config';
import AdminLayout from '../layout/AdminLayout';
import { FaPlus, FaSearch, FaCapsules, FaSyncAlt, FaTrashAlt, FaImage, FaPen, FaTimes } from 'react-icons/fa';
import { Helmet } from 'react-helmet';

const AdminManagePharmacy = () => {
    const [medicines, setMedicines] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [viewMode, setViewMode] = useState('inventory'); // 'inventory' or 'add'

    // Form state for adding medicine
    const [formData, setFormData] = useState({
        name: '', genericName: '', dosageForm: '', strength: '',
        price: '', strip: '', manufacturer: '', description: '',
        image: null
    });
    const [imagePreview, setImagePreview] = useState(null);
    const [status, setStatus] = useState({ type: '', message: '' });

    // Edit modal state
    const [editMedicine, setEditMedicine] = useState(null);
    const [editForm, setEditForm] = useState({
        name: '', genericName: '', dosageForm: '', strength: '',
        price: '', strip: '', manufacturer: '', description: ''
    });
    const [editStatus, setEditStatus] = useState({ type: '', message: '' });
    const [editSaving, setEditSaving] = useState(false);

    useEffect(() => {
        fetchMedicines();
    }, []);

    const fetchMedicines = async () => {
        try {
            const res = await api.get('/medicines');
            setMedicines(res.data);
        } catch (error) {
            console.error('Error fetching inventory:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        setFormData(prev => ({ ...prev, image: file }));
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => setImagePreview(reader.result);
            reader.readAsDataURL(file);
        }
    };

    const handleAddMedicine = async (e) => {
        e.preventDefault();
        const data = new FormData();
        Object.keys(formData).forEach(key => {
            if (formData[key]) data.append(key, formData[key]);
        });

        try {
            await api.post('/medicines/add', data, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            setStatus({ type: 'success', message: 'Medicine added successfully!' });
            setFormData({ name: '', genericName: '', dosageForm: '', strength: '', price: '', strip: '', manufacturer: '', description: '', image: null });
            setImagePreview(null);
            fetchMedicines();
            setTimeout(() => setViewMode('inventory'), 1500);
        } catch (error) {
            setStatus({ type: 'error', message: 'Failed to add medicine.' });
        }
    };

    const handleStockUpdate = async (id, currentVal) => {
        const newVal = prompt("Enter new stock count (Strips):", currentVal);
        if (newVal === null || newVal === "") return;
        
        try {
            await api.put(`/medicines/${id}`, { strip: parseInt(newVal) });
            fetchMedicines();
        } catch (error) {
            alert('Stock update failed.');
        }
    };

    const openEditModal = (m) => {
        setEditMedicine(m);
        setEditForm({
            name: m.name || '',
            genericName: m.genericName || '',
            dosageForm: m.dosageForm || '',
            strength: m.strength || '',
            price: m.price ?? '',
            strip: m.strip ?? '',
            manufacturer: m.manufacturer || '',
            description: m.description || ''
        });
        setEditStatus({ type: '', message: '' });
    };

    const closeEditModal = () => {
        setEditMedicine(null);
        setEditSaving(false);
        setEditStatus({ type: '', message: '' });
    };

    const handleEditChange = (e) => {
        const { name, value } = e.target;
        setEditForm(prev => ({ ...prev, [name]: value }));
    };

    const handleEditSubmit = async (e) => {
        e.preventDefault();
        if (!editMedicine) return;
        setEditSaving(true);
        try {
            await api.put(`/medicines/edit/${editMedicine._id}`, {
                name: editForm.name,
                genericName: editForm.genericName,
                dosageForm: editForm.dosageForm,
                strength: editForm.strength,
                price: editForm.price,
                strip: editForm.strip,
                manufacturer: editForm.manufacturer,
                description: editForm.description
            });
            await fetchMedicines();
            closeEditModal();
        } catch (error) {
            setEditSaving(false);
            setEditStatus({ type: 'error', message: 'Failed to update medicine.' });
        }
    };

    const handleDeleteMedicine = async (id, name) => {
        if (!window.confirm(`Delete "${name}" from the inventory? This action cannot be undone.`)) return;
        try {
            await api.delete(`/medicines/${id}`);
            fetchMedicines();
        } catch (error) {
            alert('Failed to delete medicine.');
        }
    };

    const filteredMedicines = medicines.filter(m => 
        m.name?.toLowerCase().includes(searchTerm.toLowerCase()) || 
        m.genericName?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <AdminLayout title="Pharmacy Management" subtitle="Manage drug inventory and clinical supply">
            <Helmet>
                <title>Pharmacy Management - HealingWave</title>
            </Helmet>

            <div className="flex items-center justify-between mb-8">
            <div className="admin-toggle-container flex bg-white dark:bg-zinc-900 p-1 rounded-xl border border-gray-200 dark:border-zinc-800 shadow-sm">
                    <button 
                        onClick={() => setViewMode('inventory')}
                        className={`flex items-center gap-2 px-6 py-2.5 rounded-lg font-semibold transition-all ${
                            viewMode === 'inventory' ? 'bg-gray-800 text-white shadow-md' : 'text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-zinc-800'
                        }`}
                    >
                        <FaCapsules /> Inventory Registry
                    </button>
                    <button 
                        onClick={() => setViewMode('add')}
                        className={`flex items-center gap-2 px-6 py-2.5 rounded-lg font-semibold transition-all ${
                            viewMode === 'add' ? 'bg-gray-800 text-white shadow-md' : 'text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-zinc-800'
                        }`}
                    >
                        <FaPlus /> Add New Drug
                    </button>
                </div>

                {viewMode === 'inventory' && (
                    <div className="relative max-w-sm w-full">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                            <FaSearch />
                        </span>
                        <input 
                            type="text" 
                            placeholder="Search by brand or generic name..." 
                            className="admin-search-input w-full pl-9 pr-4 py-2 bg-white dark:bg-zinc-800 border border-gray-200 dark:border-zinc-700 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-700/20 dark:text-gray-200 font-sans"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                )}
            </div>

            {viewMode === 'inventory' ? (
                <div className="admin-card bg-white dark:bg-zinc-900 rounded-xl border border-gray-200 dark:border-zinc-800 shadow-sm overflow-hidden">
                    <div className="overflow-x-auto min-h-[400px]">
                        <table className="w-full text-left">
                            <thead className="bg-gray-50 dark:bg-zinc-800/10 border-b border-gray-100 dark:border-zinc-800 text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest">
                                <tr>
                                    <th className="p-4">Drug Information</th>
                                    <th className="p-4">Composition</th>
                                    <th className="p-4">Pricing</th>
                                    <th className="p-4">Stock (Strips)</th>
                                    <th className="p-4 text-right">Action</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 dark:divide-zinc-800">
                                {loading ? (
                                    <tr><td colSpan="5" className="p-12 text-center text-gray-400">Loading supply chain data...</td></tr>
                                ) : filteredMedicines.length === 0 ? (
                                    <tr><td colSpan="5" className="p-12 text-center text-gray-400">Nomenclature not found.</td></tr>
                                ) : (
                                    filteredMedicines.map((m) => (
                                        <tr key={m._id} className="hover:bg-gray-100/30 dark:hover:bg-gray-900/10 transition-colors border-b border-gray-100 dark:border-zinc-800 group">
                                            <td className="p-4">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-10 h-10 rounded-lg bg-gray-100 dark:bg-zinc-800 flex items-center justify-center overflow-hidden border border-gray-200 dark:border-zinc-700">
                                                        {m.image ? <img src={`${m.image}`} className="w-full h-full object-cover" /> : <FaCapsules className="text-gray-300 dark:text-gray-600" />}
                                                    </div>
                                                    <div>
                                                        <div className="font-bold text-gray-900 dark:text-gray-100">{m.name}</div>
                                                        <div className="text-[10px] text-gray-400 dark:text-gray-500 uppercase tracking-tight">{m.manufacturer}</div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="p-4">
                                                <div className="text-sm font-medium text-gray-700 dark:text-gray-300">{m.genericName}</div>
                                                <div className="text-xs text-gray-500 dark:text-gray-400">{m.strength} | {m.dosageForm}</div>
                                            </td>
                                            <td className="p-4">
                                                <div className="font-bold text-gray-900 dark:text-gray-100">৳{m.price}</div>
                                                <div className="text-[10px] text-gray-500 dark:text-gray-400">MRPT per Strip</div>
                                            </td>
                                            <td className="p-4">
                                                <div className={`text-lg font-black ${m.strip < 10 ? 'text-red-600' : 'text-gray-800'}`}>
                                                    {m.strip}
                                                </div>
                                                {m.strip < 10 && <div className="text-[10px] text-red-500 font-bold">REORDER SOON</div>}
                                            </td>
                                            <td className="p-4 text-right">
                                                <div className="flex items-center justify-end gap-2 transition-all">
                                                    <button onClick={() => handleStockUpdate(m._id, m.strip)} title="Update Stock" className="p-2 border border-gray-200 dark:border-zinc-700 hover:bg-gray-100 dark:hover:bg-gray-900/40 rounded-lg text-gray-800 dark:text-gray-400">
                                                        <FaSyncAlt className="text-sm" />
                                                    </button>
                                                    <button onClick={() => openEditModal(m)} title="Edit Details" className="p-2 border border-gray-200 dark:border-zinc-700 hover:bg-gray-100 dark:hover:bg-gray-900/40 rounded-lg text-gray-800 dark:text-gray-400">
                                                        <FaPen className="text-sm" />
                                                    </button>
                                                    <button onClick={() => handleDeleteMedicine(m._id, m.name)} title="Delete" className="p-2 border border-red-200 dark:border-red-900/40 hover:bg-red-100 dark:hover:bg-red-900/40 rounded-lg text-red-600 dark:text-red-400">
                                                        <FaTrashAlt className="text-sm" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            ) : (
                <div className="admin-card bg-white dark:bg-zinc-900 rounded-2xl border border-gray-200 dark:border-zinc-800 shadow-sm p-8 max-w-4xl mx-auto">
                    <form onSubmit={handleAddMedicine} className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-4">
                            <div>
                                <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 mb-1">DRUG NAME</label>
                                <input name="name" value={formData.name} onChange={handleInputChange} className="w-full p-2.5 bg-gray-50 dark:bg-zinc-800 border border-gray-200 dark:border-zinc-700 rounded-lg focus:ring-2 focus:ring-gray-700/20 dark:text-gray-200 font-sans" required />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 mb-1">GENERIC NAME</label>
                                <input name="genericName" value={formData.genericName} onChange={handleInputChange} className="w-full p-2.5 bg-gray-50 dark:bg-zinc-800 border border-gray-200 dark:border-zinc-700 rounded-lg focus:ring-2 focus:ring-gray-700/20 dark:text-gray-200 font-sans" required />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 mb-1">STRENGTH</label>
                                    <input placeholder="e.g. 500mg" name="strength" value={formData.strength} onChange={handleInputChange} className="w-full p-2.5 bg-gray-50 dark:bg-zinc-800 border border-gray-200 dark:border-zinc-700 rounded-lg dark:text-gray-200 font-sans" required />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 mb-1">DOSAGE FORM</label>
                                    <input placeholder="e.g. Tablet" name="dosageForm" value={formData.dosageForm} onChange={handleInputChange} className="w-full p-2.5 bg-gray-50 dark:bg-zinc-800 border border-gray-200 dark:border-zinc-700 rounded-lg dark:text-gray-200 font-sans" required />
                                </div>
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 mb-1">MANUFACTURER</label>
                                <input name="manufacturer" value={formData.manufacturer} onChange={handleInputChange} className="w-full p-2.5 bg-gray-50 dark:bg-zinc-800 border border-gray-200 dark:border-zinc-700 rounded-lg dark:text-gray-200 font-sans" required />
                            </div>
                        </div>

                        <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 mb-1">UNIT PRICE (৳)</label>
                                    <input type="number" name="price" value={formData.price} onChange={handleInputChange} className="w-full p-2.5 bg-gray-50 dark:bg-zinc-800 border border-gray-200 dark:border-zinc-700 rounded-lg dark:text-gray-200 font-sans" required />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 mb-1">INITIAL STOCK</label>
                                    <input type="number" name="strip" value={formData.strip} onChange={handleInputChange} className="w-full p-2.5 bg-gray-50 dark:bg-zinc-800 border border-gray-200 dark:border-zinc-700 rounded-lg dark:text-gray-200 font-sans" required />
                                </div>
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 mb-1">PRODUCT IMAGE</label>
                                <div className="border-2 border-dashed border-gray-200 dark:border-zinc-700 rounded-xl p-4 flex flex-col items-center justify-center gap-2 hover:border-gray-300 dark:hover:border-gray-700 transition-all cursor-pointer relative overflow-hidden h-32">
                                    {imagePreview ? (
                                        <img src={imagePreview} className="absolute inset-0 w-full h-full object-contain" />
                                    ) : (
                                        <>
                                            <FaImage className="text-gray-300 text-2xl" />
                                            <span className="text-xs text-gray-400">Click to upload 1:1 image</span>
                                        </>
                                    )}
                                    <input type="file" onChange={handleFileChange} className="absolute inset-0 opacity-0 cursor-pointer" />
                                </div>
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 mb-1">DESCRIPTION</label>
                                <textarea name="description" value={formData.description} onChange={handleInputChange} className="w-full p-2.5 bg-gray-50 dark:bg-zinc-800 border border-gray-200 dark:border-zinc-700 rounded-lg h-24 resize-none dark:text-gray-200 font-sans"></textarea>
                            </div>
                        </div>

                        <div className="md:col-span-2 pt-4">
                            {status.message && (
                                <div className={`mb-4 px-4 py-2 rounded-lg text-sm font-bold ${status.type === 'success' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
                                    {status.message}
                                </div>
                            )}
                            <button type="submit" className="w-full py-3 bg-gray-800 text-white font-black rounded-xl shadow-lg shadow-gray-200 hover:bg-gray-900 transition-all">
                                COMPLETE REGISTRATION
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {editMedicine && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm" onClick={closeEditModal}>
                    <div className="bg-white dark:bg-zinc-900 rounded-2xl border border-gray-200 dark:border-zinc-800 shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-zinc-800 sticky top-0 bg-white dark:bg-zinc-900 z-10">
                            <div className="flex items-center gap-3">
                                <div className="w-9 h-9 rounded-lg bg-gray-100 dark:bg-zinc-800 flex items-center justify-center text-gray-800 dark:text-gray-300">
                                    <FaPen className="text-sm" />
                                </div>
                                <div>
                                    <h3 className="font-bold text-gray-900 dark:text-gray-100 leading-tight">Edit Medicine</h3>
                                    <p className="text-[11px] text-gray-400 uppercase tracking-tight">{editMedicine.name}</p>
                                </div>
                            </div>
                            <button onClick={closeEditModal} className="p-2 hover:bg-gray-100 dark:hover:bg-zinc-800 rounded-lg text-gray-500">
                                <FaTimes />
                            </button>
                        </div>

                        <form onSubmit={handleEditSubmit} className="p-6 grid grid-cols-1 md:grid-cols-2 gap-5">
                            <div>
                                <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 mb-1">DRUG NAME</label>
                                <input name="name" value={editForm.name} onChange={handleEditChange} className="w-full p-2.5 bg-gray-50 dark:bg-zinc-800 border border-gray-200 dark:border-zinc-700 rounded-lg focus:ring-2 focus:ring-gray-700/20 dark:text-gray-200 font-sans" required />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 mb-1">GENERIC NAME</label>
                                <input name="genericName" value={editForm.genericName} onChange={handleEditChange} className="w-full p-2.5 bg-gray-50 dark:bg-zinc-800 border border-gray-200 dark:border-zinc-700 rounded-lg focus:ring-2 focus:ring-gray-700/20 dark:text-gray-200 font-sans" required />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 mb-1">STRENGTH</label>
                                <input name="strength" value={editForm.strength} onChange={handleEditChange} className="w-full p-2.5 bg-gray-50 dark:bg-zinc-800 border border-gray-200 dark:border-zinc-700 rounded-lg focus:ring-2 focus:ring-gray-700/20 dark:text-gray-200 font-sans" />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 mb-1">DOSAGE FORM</label>
                                <input name="dosageForm" value={editForm.dosageForm} onChange={handleEditChange} className="w-full p-2.5 bg-gray-50 dark:bg-zinc-800 border border-gray-200 dark:border-zinc-700 rounded-lg focus:ring-2 focus:ring-gray-700/20 dark:text-gray-200 font-sans" />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 mb-1">UNIT PRICE (৳)</label>
                                <input type="number" name="price" value={editForm.price} onChange={handleEditChange} className="w-full p-2.5 bg-gray-50 dark:bg-zinc-800 border border-gray-200 dark:border-zinc-700 rounded-lg focus:ring-2 focus:ring-gray-700/20 dark:text-gray-200 font-sans" required />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 mb-1">STOCK (STRIPS)</label>
                                <input type="number" name="strip" value={editForm.strip} onChange={handleEditChange} className="w-full p-2.5 bg-gray-50 dark:bg-zinc-800 border border-gray-200 dark:border-zinc-700 rounded-lg focus:ring-2 focus:ring-gray-700/20 dark:text-gray-200 font-sans" required />
                            </div>
                            <div className="md:col-span-2">
                                <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 mb-1">MANUFACTURER</label>
                                <input name="manufacturer" value={editForm.manufacturer} onChange={handleEditChange} className="w-full p-2.5 bg-gray-50 dark:bg-zinc-800 border border-gray-200 dark:border-zinc-700 rounded-lg focus:ring-2 focus:ring-gray-700/20 dark:text-gray-200 font-sans" />
                            </div>
                            <div className="md:col-span-2">
                                <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 mb-1">DESCRIPTION</label>
                                <textarea name="description" value={editForm.description} onChange={handleEditChange} className="w-full p-2.5 bg-gray-50 dark:bg-zinc-800 border border-gray-200 dark:border-zinc-700 rounded-lg h-24 resize-none focus:ring-2 focus:ring-gray-700/20 dark:text-gray-200 font-sans"></textarea>
                            </div>

                            {editStatus.message && (
                                <div className="md:col-span-2 px-4 py-2 rounded-lg text-sm font-bold bg-red-50 text-red-700">
                                    {editStatus.message}
                                </div>
                            )}

                            <div className="md:col-span-2 flex items-center justify-end gap-3 pt-2">
                                <button type="button" onClick={closeEditModal} className="px-5 py-2.5 rounded-xl font-bold text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-zinc-700 hover:bg-gray-50 dark:hover:bg-zinc-800 transition-all">
                                    Cancel
                                </button>
                                <button type="submit" disabled={editSaving} className="px-6 py-2.5 bg-gray-800 text-white font-bold rounded-xl shadow-lg shadow-gray-200 hover:bg-gray-900 transition-all disabled:opacity-60">
                                    {editSaving ? 'Saving...' : 'Save Changes'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </AdminLayout>
    );
};

export default AdminManagePharmacy;
